import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, PlaylistAddCheck, PlayArrow, DoneAll, Cancel, Edit as EditIcon, DeleteOutline, ChevronLeft, ChevronRight, Block as BlockIcon, Notes as NotesIcon, PauseCircleOutline, Replay, Visibility, VisibilityOff } from '@mui/icons-material';
import { DndContext, DragEndEvent, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import api from '../services/api';
import { ProductionOrder, Product, Recipe, Material, Development } from '../types';
import TextField from '../components/common/TextField';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'Planned', label: 'Planificada' },
  { value: 'InProgress', label: 'En progreso' },
  { value: 'Semielaborado', label: 'Semielaborado' },
  { value: 'Completed', label: 'Completada' },
  { value: 'Cancelled', label: 'Cancelada' },
];

// Semántica acordada con el cliente: verde = producida, amarillo = en proceso,
// azul = planificada, gris = cancelada / día bloqueado, violeta = semielaborado
// (producción física avanzada o terminada, pendiente de control de calidad/etiquetado).
const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'secondary'> = {
  Planned: 'primary',
  InProgress: 'warning',
  Semielaborado: 'secondary',
  Completed: 'success',
  Cancelled: 'default',
  PausedStock: 'error',
};

const today = () => new Date().toISOString().slice(0, 10);
// Fecha-solo: formatear por string, sin pasar por Date (una fecha UTC 00:00 se
// corría un día para atrás en Argentina con toLocaleDateString).
const formatDate = (value?: string) => {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};
// Vencimiento de producción se carga y muestra solo mes/año (no interesa el día).
const formatMonthYear = (value?: string | null) => {
  if (!value) return '—';
  const [y, m] = value.slice(0, 10).split('-');
  return `${m}/${y}`;
};
// El input type="month" entrega "YYYY-MM"; el backend espera una fecha completa
// parseable, así que se completa con el día 1 del mes al enviar.
const monthToFullDate = (value?: string) => (value ? `${value}-01` : undefined);

const STATUS_LABELS: Record<string, string> = {
  Planned: 'Planificada',
  InProgress: 'En progreso',
  Semielaborado: 'Semielaborado',
  Completed: 'Completada',
  Cancelled: 'Cancelada',
  PausedStock: 'Pausada (sin stock)',
};
const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const WEEKDAYS_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const pad2 = (n: number) => String(n).padStart(2, '0');
const isoOf = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Chip de OP arrastrable en el calendario. Solo planificadas / en progreso se
// pueden reprogramar (el backend rechaza el resto). El clic normal sigue
// funcionando: el sensor recién activa el drag después de moverse unos píxeles.
const DraggableOrderChip: React.FC<{ order: ProductionOrder; tooltip: string; onClick: () => void }> = ({ order, tooltip, onClick }) => {
  const movable = order.status === 'Planned' || order.status === 'InProgress' || order.status === 'Semielaborado';
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `op-${order.id}`,
    data: { order },
    disabled: !movable,
  });
  return (
    <Tooltip title={tooltip + (movable ? ' — arrastrá para reprogramar' : '')}>
      <Box
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        sx={{
          display: 'flex',
          position: 'relative',
          touchAction: 'none',
          transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
          zIndex: isDragging ? 1300 : undefined,
          opacity: isDragging ? 0.85 : 1,
          cursor: movable ? 'grab' : 'pointer',
        }}
      >
        <Chip
          size="small"
          color={STATUS_COLORS[order.status] || 'default'}
          label={order.productName}
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          sx={{ maxWidth: '100%', height: 20, cursor: 'inherit', '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
        />
      </Box>
    </Tooltip>
  );
};

// Celda de día que acepta el drop de una OP.
const DroppableDayCell: React.FC<{ iso: string; title: string; onClick: (e: React.MouseEvent<HTMLElement>) => void; sx: any; children: React.ReactNode }> =
  ({ iso, title, onClick, sx, children }) => {
    const { setNodeRef, isOver } = useDroppable({ id: `day-${iso}`, data: { iso } });
    return (
      <Box ref={setNodeRef} onClick={onClick} title={title} sx={{ ...sx, ...(isOver ? { borderColor: 'primary.main', borderWidth: 2, bgcolor: 'action.selected' } : {}) }}>
        {children}
      </Box>
    );
  };

// Roles que pueden ajustar stock y editar lotes (espejo del control server-side).
const STOCK_ROLES = ['admin', 'manager', 'propietario', 'gerente', 'logistica', 'calidad'];
// Roles que pueden ajustar la cantidad planificada con la orden ya en curso (espejo
// de StockPermissions.CanEditInProgressQuantity).
const QUANTITY_EDIT_ROLES = ['admin', 'manager', 'propietario', 'gerente', 'logistica', 'calidad', 'produccion'];

export const ProductionOrdersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { user } = useSelector((s: RootState) => s.auth);
  const canEditBatches = STOCK_ROLES.includes((user?.role || '').toLowerCase());
  const canEditInProgressQuantity = QUANTITY_EDIT_ROLES.includes((user?.role || '').toLowerCase());
  const navState = location.state as { productId?: number; recipeId?: number; suggestedQty?: number } | null;

  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<ProductionOrder | null>(null);
  const [reservationsOpen, setReservationsOpen] = useState(false);
  const [tab, setTab] = useState<'orders' | 'calendar' | 'batches' | 'developments'>('orders');
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  // Bloqueos y notas de día (reusan los eventos del calendario). Son cosas distintas:
  // "note" es solo una anotación visual; "block" además impide programar OPs ese día
  // (validado también en el servidor).
  const [dayBlocks, setDayBlocks] = useState<{ id: number; title: string; iso: string; kind: 'block' | 'note' }[]>([]);
  const [dayMenu, setDayMenu] = useState<{ anchorEl: HTMLElement; iso: string } | null>(null);
  const [blockDialog, setBlockDialog] = useState<{ iso: string; text: string; kind: 'block' | 'note' } | null>(null);
  type BatchRow = { id: number; publicId?: string | null; lotCode?: string | null; materialId: number; materialName?: string | null; quantity: number; unitCost: number; expirationDate?: string | null; purchaseReference?: string | null; supplierName?: string | null; createdAt: string; consumedBy: Array<{ productionOrderId: number; quantityUsed: number; createdAt: string }> };
  const [batches, setBatches] = useState<BatchRow[]>([]);
  // Edición de lote/vencimiento de un lote de MP (1.1.b)
  const [batchEdit, setBatchEdit] = useState<{ id: number; lotCode: string; expirationDate: string } | null>(null);
  const [batchEditSaving, setBatchEditSaving] = useState(false);
  const saveBatchEdit = async () => {
    if (!batchEdit) return;
    setBatchEditSaving(true);
    try {
      await api.updateMaterialBatch(batchEdit.id, {
        lotCode: batchEdit.lotCode,
        expirationDate: batchEdit.expirationDate || null,
      });
      setBatchEdit(null);
      await loadBatches();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo editar el lote');
    } finally {
      setBatchEditSaving(false);
    }
  };
  const [batchSearch, setBatchSearch] = useState('');
  const [batchOnlyAvailable, setBatchOnlyAvailable] = useState(true);
  const [batchExpFrom, setBatchExpFrom] = useState('');
  const [batchExpTo, setBatchExpTo] = useState('');
  const [batchProdOrderId, setBatchProdOrderId] = useState<string>('');
  const [batchesLoading, setBatchesLoading] = useState(false);
  const loadBatches = async () => {
    setBatchesLoading(true);
    try {
      const res = await api.getMaterialBatches({
        search: batchSearch || undefined,
        onlyAvailable: batchOnlyAvailable,
        expirationFrom: batchExpFrom || undefined,
        expirationTo: batchExpTo || undefined,
        productionOrderId: batchProdOrderId ? Number(batchProdOrderId) : undefined,
      });
      setBatches(res.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar los lotes');
    } finally {
      setBatchesLoading(false);
    }
  };
  const deleteBatch = async (b: BatchRow) => {
    const label = b.lotCode || b.publicId || `#${b.id}`;
    if (!window.confirm(`¿Eliminar el lote ${label}? Se descontará su stock (${b.quantity}) del material y no se puede deshacer.`)) return;
    try {
      await api.deleteMaterialBatch(b.id);
      await loadBatches();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo eliminar el lote');
    }
  };
  useEffect(() => {
    if (tab === 'batches') loadBatches();
    if (tab === 'developments') loadDevelopments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrder | null>(null);

  const [products, setProducts] = useState<Product[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [form, setForm] = useState<{ orderNumber: string; productId: string; recipeId: string; quantity: number; scheduledDate: string; notes: string; lotCode: string; expirationDate: string }>({
    orderNumber: '',
    productId: '',
    recipeId: '',
    quantity: 1,
    scheduledDate: today(),
    notes: '',
    lotCode: '',
    expirationDate: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [prefillApplied, setPrefillApplied] = useState(false);

  const canCreate = useMemo(() => {
    return form.productId && form.recipeId && form.quantity > 0;
  }, [form]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getProductionOrders({ status: statusFilter || undefined, includeHidden: showHidden || undefined });
      setOrders(res.items || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron cargar las órdenes');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await api.getProducts({ page: 1, pageSize: 200, search: '' });
      setProducts(res.products || []);
    } catch {
      // ignore
    }
  };

  const loadRecipes = async (productId: number) => {
    try {
      const res = await api.getRecipes({ page: 1, pageSize: 200, productId });
      setRecipes(res.recipes || []);
    } catch {
      setRecipes([]);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [statusFilter, showHidden]);

  useEffect(() => {
    loadProducts();
  }, []);

  // Pre-fill form from navigation state (e.g., from Dashboard)
  useEffect(() => {
    if (navState?.productId && products.length > 0 && !prefillApplied) {
      const productIdStr = String(navState.productId);
      setForm(prev => ({
        ...prev,
        productId: productIdStr,
        recipeId: navState.recipeId ? String(navState.recipeId) : '',
        quantity: navState.suggestedQty || 1,
      }));
      loadRecipes(navState.productId);
      setCreateOpen(true);
      setPrefillApplied(true);
    }
  }, [navState, products, prefillApplied]);

  const handleProductChange = async (value: string) => {
    setForm((prev) => ({ ...prev, productId: value, recipeId: '' }));
    setRecipes([]);
    if (value) {
      await loadRecipes(Number(value));
    }
  };

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setEditingOrder(null);
    setForm({ orderNumber: '', productId: '', recipeId: '', quantity: 1, scheduledDate: today(), notes: '', lotCode: '', expirationDate: '' });
    setRecipes([]);
  };

  const openEdit = async (order: ProductionOrder) => {
    setError(null);
    setSuccess(null);
    setEditingOrder(order);
    setForm({
      orderNumber: order.orderNumber || '',
      productId: String(order.productId),
      recipeId: order.recipeId ? String(order.recipeId) : '',
      quantity: order.quantity,
      scheduledDate: (order.scheduledDate || today()).slice(0, 10),
      notes: order.notes || '',
      lotCode: order.lotCode || '',
      expirationDate: order.expirationDate ? order.expirationDate.slice(0, 7) : '',
    });
    await loadRecipes(order.productId);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingOrder) {
        await api.updateProductionOrder(editingOrder.id, {
          orderNumber: form.orderNumber.trim() || undefined,
          quantity: form.quantity,
          recipeId: form.recipeId ? Number(form.recipeId) : undefined,
          scheduledDate: form.scheduledDate,
          notes: form.notes || '',
          lotCode: form.lotCode || '',
          expirationDate: monthToFullDate(form.expirationDate),
        });
        setSuccess('Orden actualizada correctamente');
      } else {
        const created = await api.createProductionOrder({
          productId: Number(form.productId),
          recipeId: Number(form.recipeId),
          quantity: form.quantity,
          scheduledDate: form.scheduledDate,
          notes: form.notes || undefined,
          lotCode: form.lotCode || undefined,
          expirationDate: monthToFullDate(form.expirationDate),
        });
        // No se bloquea la creacion por falta de stock (a pedido): la orden queda
        // "Pausada" sin descontar nada, en vez de no crearse.
        setSuccess(created.status === 'PausedStock'
          ? `Orden creada, pero quedó pausada por falta de stock (${created.stockShortage}). Podés reintentar cuando llegue el material.`
          : 'Orden creada correctamente');
      }
      closeCreateDialog();
      await loadOrders();
    } catch (err: any) {
      setError(err?.response?.data?.message || (editingOrder ? 'Error al actualizar la orden' : 'Error al crear la orden'));
    } finally {
      setSaving(false);
    }
  };

  // Ocultar del listado (no borra ni toca stock/lotes/trazabilidad).
  const hideOrder = (order: ProductionOrder) =>
    confirmAction(
      `¿Ocultar ${order.orderNumber || 'esta orden'} del listado? No se modifica el stock ni se pierde ningún dato; se puede volver a mostrar.`,
      () => api.hideProductionOrder(order.id),
    );

  const unhideOrder = (order: ProductionOrder) =>
    confirmAction('¿Volver a mostrar esta orden en el listado?', () => api.unhideProductionOrder(order.id));

  const hideAllCompleted = async () => {
    if (!window.confirm('¿Ocultar TODAS las órdenes completadas del listado? No se modifica el stock ni se pierde ningún dato; se pueden volver a mostrar con "Mostrar ocultas". Las pausadas, en curso y planificadas no se tocan.')) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await api.hideCompletedProductionOrders();
      await loadOrders();
      setSuccess(`${res.hidden} órdenes completadas ocultadas`);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron ocultar las órdenes');
    }
  };

  // Desarrollos/pruebas de producto (pestaña aparte, pedido de Micaela): descuentan
  // materia prima sin lote ni orden de producción. Un desarrollo puede tener varias
  // muestras a lo largo del tiempo, todas juntas en el mismo registro.
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [devLoading, setDevLoading] = useState(false);
  const [devCustomers, setDevCustomers] = useState<{ id: number; legalName: string }[]>([]);
  const [devMaterials, setDevMaterials] = useState<Material[]>([]);
  const [devCustomerFilter, setDevCustomerFilter] = useState<{ id: number; legalName: string } | null>(null);
  const [expandedDev, setExpandedDev] = useState<number | null>(null);

  type DevMaterialRow = { materialId: number | ''; quantity: string };
  const emptyDevRow = (): DevMaterialRow => ({ materialId: '', quantity: '' });

  const [newDevDialog, setNewDevDialog] = useState<{
    name: string;
    customer: { id: number; legalName: string } | null;
    notes: string;
    elaborationDate: string;
    sampleNotes: string;
    rows: DevMaterialRow[];
  } | null>(null);
  const [newDevSaving, setNewDevSaving] = useState(false);

  const [addSampleDialog, setAddSampleDialog] = useState<{
    developmentId: number;
    developmentName: string;
    elaborationDate: string;
    notes: string;
    rows: DevMaterialRow[];
  } | null>(null);
  const [addSampleSaving, setAddSampleSaving] = useState(false);

  const loadDevelopments = async () => {
    setDevLoading(true);
    setError(null);
    try {
      const [devRes, custRes, matRes] = await Promise.all([
        api.getDevelopments(devCustomerFilter ? { customerId: devCustomerFilter.id } : undefined),
        devCustomers.length === 0 ? api.getCustomers({ page: 1, pageSize: 500 }) : Promise.resolve(null),
        devMaterials.length === 0 ? api.getMaterials({ page: 1, pageSize: 500 }) : Promise.resolve(null),
      ]);
      setDevelopments(devRes.items || []);
      if (custRes) setDevCustomers((custRes.customers || []).map((c: any) => ({ id: c.id, legalName: c.legalName })));
      if (matRes) setDevMaterials(matRes.materials || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron cargar los desarrollos');
    } finally {
      setDevLoading(false);
    }
  };

  const openNewDevelopment = () => {
    setNewDevDialog({ name: '', customer: null, notes: '', elaborationDate: today(), sampleNotes: '', rows: [emptyDevRow()] });
  };

  const rowsToUsages = (rows: DevMaterialRow[]) =>
    rows.filter(r => r.materialId !== '' && Number(r.quantity) > 0).map(r => ({ materialId: Number(r.materialId), quantity: Number(r.quantity) }));

  const saveNewDevelopment = async () => {
    if (!newDevDialog) return;
    const usages = rowsToUsages(newDevDialog.rows);
    if (!newDevDialog.name.trim()) { setError('Ponele un nombre al desarrollo'); return; }
    if (!newDevDialog.customer) { setError('Elegí un cliente'); return; }
    if (usages.length === 0) { setError('Agregá al menos una materia prima con cantidad'); return; }
    setNewDevSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.createDevelopment({
        name: newDevDialog.name.trim(),
        customerId: newDevDialog.customer.id,
        notes: newDevDialog.notes || undefined,
        elaborationDate: newDevDialog.elaborationDate || undefined,
        sampleNotes: newDevDialog.sampleNotes || undefined,
        materialUsages: usages,
      });
      setNewDevDialog(null);
      await loadDevelopments();
      setSuccess('Desarrollo creado');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo crear el desarrollo');
    } finally {
      setNewDevSaving(false);
    }
  };

  const openAddSample = (dev: Development) => {
    setAddSampleDialog({ developmentId: dev.id, developmentName: dev.name, elaborationDate: today(), notes: '', rows: [emptyDevRow()] });
  };

  const saveAddSample = async () => {
    if (!addSampleDialog) return;
    const usages = rowsToUsages(addSampleDialog.rows);
    if (usages.length === 0) { setError('Agregá al menos una materia prima con cantidad'); return; }
    setAddSampleSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.addDevelopmentSample(addSampleDialog.developmentId, {
        elaborationDate: addSampleDialog.elaborationDate || undefined,
        notes: addSampleDialog.notes || undefined,
        materialUsages: usages,
      });
      setAddSampleDialog(null);
      await loadDevelopments();
      setSuccess('Muestra agregada');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo agregar la muestra');
    } finally {
      setAddSampleSaving(false);
    }
  };

  const deleteDevSample = async (sampleId: number) => {
    if (!window.confirm('¿Borrar esta muestra? La materia prima que consumió vuelve al stock.')) return;
    setError(null);
    setSuccess(null);
    try {
      await api.deleteDevelopmentSample(sampleId);
      await loadDevelopments();
      setSuccess('Muestra borrada, stock devuelto');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo borrar la muestra');
    }
  };

  const deleteDevelopment = async (dev: Development) => {
    if (!window.confirm(`¿Borrar el desarrollo "${dev.name}"?`)) return;
    setError(null);
    setSuccess(null);
    try {
      await api.deleteDevelopment(dev.id);
      await loadDevelopments();
      setSuccess('Desarrollo borrado');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo borrar el desarrollo');
    }
  };

  const deleteOrder = (order: ProductionOrder) =>
    confirmAction(
      `¿Borrar la orden de "${order.productName}"? Se devolverá al stock cualquier material reservado.`,
      () => api.deleteProductionOrder(order.id),
    );

  const confirmAction = async (message: string, fn: () => Promise<void>) => {
    if (!window.confirm(message)) return;
    setError(null);
    setSuccess(null);
    try {
      await fn();
      await loadOrders();
      setSuccess('Operación realizada correctamente');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo completar la acción');
    }
  };

  const startOrder = (order: ProductionOrder) =>
    confirmAction('¿Iniciar producción?', () => api.startProductionOrder(order.id));

  // OP "Pausada" por falta de stock (se creó igual, sin descontar nada): reintenta
  // reservar ahora que puede haber llegado material.
  const retryStock = (order: ProductionOrder) =>
    confirmAction('¿Reintentar reservar stock para esta orden?', () => api.retryProductionOrderStock(order.id).then(() => undefined));

  // Dialog de Completar: muestra la propuesta FEFO por material y permite editar
  // la asignación lote por lote antes de confirmar.
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeOrderState, setCompleteOrderState] = useState<ProductionOrder | null>(null);
  type CompleteRow = {
    materialId: number;
    materialName: string;
    quantityRequired: number;
    batches: Array<{ batchId: number; publicId?: string | null; availableQuantity: number; assigned: number; unitCost: number; expirationDate?: string | null }>;
    // "Sin lote": la porción del stock total del material que nunca se cargó con un
    // lote (ej. tapas/empaque que vienen sin lote del proveedor). Se puede asignar
    // igual que un lote más, mezclado con lotes puntuales (pedido de Mica/Lucas).
    untrackedAvailable: number;
    untrackedAssigned: number;
  };
  type CompleteProductRow = {
    productId: number;
    productName: string;
    quantityRequired: number;
    batches: Array<{ batchId: number; publicId?: string | null; availableQuantity: number; assigned: number; unitCost: number; expirationDate?: string | null }>;
  };
  const [completeRows, setCompleteRows] = useState<CompleteRow[]>([]);
  // Componentes tipo PRODUCTO (granel/semielaborado): misma lógica de asignación
  // manual que los materiales (pedido de la planilla: poder elegir otro lote,
  // ej. liquidar uno parcial, en vez de solo la propuesta FIFO automática).
  const [completeProductRows, setCompleteProductRows] = useState<CompleteProductRow[]>([]);
  const [completeSaving, setCompleteSaving] = useState(false);
  const [completeLotCode, setCompleteLotCode] = useState('');
  const [completeExpirationDate, setCompleteExpirationDate] = useState('');
  // Cantidad real producida / merma (pedido reunión Mica/Lucas + planilla).
  const [completeActualQuantity, setCompleteActualQuantity] = useState('');
  const [completeYieldReason, setCompleteYieldReason] = useState('');
  // Muestra de retención/análisis (se descuentan del lote, no quedan vendibles).
  const [completeRetentionQty, setCompleteRetentionQty] = useState('');
  const [completeAnalysisQty, setCompleteAnalysisQty] = useState('');

  const completeOrder = async (order: ProductionOrder) => {
    setError(null);
    // Si todavía falta stock (se inició igual, aunque estaba pausada), avisar acá
    // y no abrir el diálogo vacío: el backend igual la rechaza, pero así es claro.
    if (order.stockShortage) {
      setError(`Todavía falta stock, no se puede completar: ${order.stockShortage}`);
      return;
    }
    try {
      const proposed = await api.getProposedConsumption(order.id);
      setCompleteOrderState(order);
      setCompleteLotCode(order.lotCode || '');
      setCompleteExpirationDate(order.expirationDate ? order.expirationDate.slice(0, 7) : '');
      setCompleteActualQuantity(String(order.quantity));
      setCompleteYieldReason('');
      setCompleteRetentionQty('');
      setCompleteAnalysisQty('');
      setCompleteRows(proposed.materials.map(m => {
        const proposedFromBatches = m.batches.reduce((s, b) => s + (Number(b.proposedQuantity) || 0), 0);
        // Si los lotes no alcanzan a cubrir lo requerido, proponemos por default que el
        // resto salga del stock "sin lote" (mismo comportamiento silencioso que ya tenía
        // el sistema), pero ahora queda visible y editable en vez de ser implícito.
        const defaultUntracked = Math.max(0, Math.min(m.untrackedAvailable, m.quantityRequired - proposedFromBatches));
        return {
          materialId: m.materialId,
          materialName: m.materialName,
          quantityRequired: m.quantityRequired,
          untrackedAvailable: m.untrackedAvailable,
          untrackedAssigned: defaultUntracked,
          batches: m.batches.map(b => ({
            batchId: b.batchId,
            publicId: b.publicId,
            availableQuantity: b.availableQuantity,
            assigned: b.proposedQuantity,
            unitCost: b.unitCost,
            expirationDate: b.expirationDate,
          })),
        };
      }));
      setCompleteProductRows((proposed.products || []).map(m => ({
        productId: m.productId,
        productName: m.productName,
        quantityRequired: m.quantityRequired,
        batches: m.batches.map(b => ({
          batchId: b.batchId,
          publicId: b.publicId,
          availableQuantity: b.availableQuantity,
          assigned: b.proposedQuantity,
          unitCost: b.unitCost,
          expirationDate: b.expirationDate,
        })),
      })));
      setCompleteOpen(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cargar la propuesta de consumo');
    }
  };

  const confirmComplete = async () => {
    if (!completeOrderState) return;
    // Validar que cada material/producto asigne exactamente lo requerido
    for (const row of [...completeRows, ...completeProductRows]) {
      const isMaterial = 'materialName' in row;
      const label = isMaterial ? (row as CompleteRow).materialName : (row as CompleteProductRow).productName;
      const untracked = isMaterial ? Number((row as CompleteRow).untrackedAssigned) || 0 : 0;
      const sum = row.batches.reduce((s, b) => s + (Number(b.assigned) || 0), 0) + untracked;
      if (Math.abs(sum - row.quantityRequired) > 0.001) {
        setError(`La asignación de ${label} suma ${sum} pero se requieren ${row.quantityRequired}.`);
        return;
      }
      if (row.batches.some(b => b.assigned > b.availableQuantity + 0.001)) {
        setError(`Algún lote de ${label} excede su stock disponible.`);
        return;
      }
      if (isMaterial && untracked > (row as CompleteRow).untrackedAvailable + 0.001) {
        setError(`La cantidad sin lote de ${label} excede el stock suelto disponible.`);
        return;
      }
    }
    const actualQty = completeActualQuantity === '' ? undefined : Number(completeActualQuantity);
    if (actualQty !== undefined && actualQty < 0) {
      setError('La cantidad real producida no puede ser negativa');
      return;
    }
    if (actualQty !== undefined && actualQty < completeOrderState.quantity && !completeYieldReason.trim()) {
      setError('Hubo merma respecto de lo planificado: indicá el motivo');
      return;
    }
    const retentionQty = Number(completeRetentionQty || 0);
    const analysisQty = Number(completeAnalysisQty || 0);
    const producedQty = actualQty ?? completeOrderState.quantity;
    if (retentionQty + analysisQty > producedQty) {
      setError('La muestra de retención + análisis no puede superar la cantidad producida');
      return;
    }
    setCompleteSaving(true);
    try {
      const manualAllocations = completeRows.map(r => ({
        materialId: r.materialId,
        batchAllocations: r.batches.filter(b => b.assigned > 0).map(b => ({ batchId: b.batchId, quantity: Number(b.assigned) })),
        untrackedQuantity: Number(r.untrackedAssigned) || undefined,
      }));
      const manualProductAllocations = completeProductRows.map(r => ({
        productId: r.productId,
        batchAllocations: r.batches.filter(b => b.assigned > 0).map(b => ({ batchId: b.batchId, quantity: Number(b.assigned) })),
      }));
      await api.completeProductionOrder(completeOrderState.id, {
        manualAllocations,
        manualProductAllocations,
        lotCode: completeLotCode || undefined,
        expirationDate: monthToFullDate(completeExpirationDate),
        actualQuantity: actualQty,
        yieldVarianceReason: completeYieldReason || undefined,
        retentionQuantity: retentionQty || undefined,
        analysisQuantity: analysisQty || undefined,
      });
      setCompleteOpen(false);
      setCompleteOrderState(null);
      setCompleteRows([]);
      setCompleteProductRows([]);
      await loadOrders();
      setSuccess('Orden completada');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al completar la orden');
    } finally {
      setCompleteSaving(false);
    }
  };

  const cancelOrder = (order: ProductionOrder) =>
    confirmAction('¿Cancelar la orden y liberar las reservas?', () => api.cancelProductionOrder(order.id));

  // Pausar como "Semielaborado": producción física avanzada/terminada, pendiente de
  // control de calidad/etiquetado. Se puede anotar en qué quedó (texto libre).
  // Usa un diálogo propio (no window.prompt): varios navegadores/entornos embebidos
  // no soportan prompt() y tiran "prompt() is not supported".
  const [semielaboradoDialog, setSemielaboradoDialog] = useState<{ order: ProductionOrder; note: string } | null>(null);
  const [semielaboradoSaving, setSemielaboradoSaving] = useState(false);

  const pauseSemielaborado = (order: ProductionOrder) => {
    setSemielaboradoDialog({ order, note: '' });
  };

  const confirmPauseSemielaborado = async () => {
    if (!semielaboradoDialog) return;
    setSemielaboradoSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.pauseSemielaboradoOrder(semielaboradoDialog.order.id, semielaboradoDialog.note || undefined);
      setSemielaboradoDialog(null);
      await loadOrders();
      setSuccess('Orden pausada como semielaborado');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo pausar la orden');
    } finally {
      setSemielaboradoSaving(false);
    }
  };

  const openReservationsDialog = (order: ProductionOrder) => {
    setSelectedOrder(order);
    setReservationsOpen(true);
  };

  const filteredOrders = orders;

  // ─── Vista calendario ────────────────────────────────────────────────────
  // Grilla mensual (lunes a domingo) con las OPs en su fecha programada,
  // coloreadas por estado (ver STATUS_COLORS y la leyenda del calendario).
  const calendarWeeks = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7; // lunes = 0
    const weeks: { date: Date; iso: string; inMonth: boolean }[][] = [];
    for (let w = 0; w < 6; w++) {
      const days: { date: Date; iso: string; inMonth: boolean }[] = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(year, month, 1 - startOffset + (w * 7 + d));
        days.push({ date, iso: isoOf(date), inMonth: date.getMonth() === month });
      }
      weeks.push(days);
    }
    return weeks;
  }, [calendarMonth]);

  const ordersByDay = useMemo(() => {
    const map = new Map<string, ProductionOrder[]>();
    for (const o of orders) {
      const iso = (o.scheduledDate || '').slice(0, 10);
      if (!iso) continue;
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso)!.push(o);
    }
    return map;
  }, [orders]);

  const todayIso = today();
  const shiftMonth = (delta: number) => setCalendarMonth(m => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  const onCalendarOrderClick = (o: ProductionOrder) => {
    if (o.status === 'Planned' || o.status === 'InProgress' || o.status === 'Semielaborado') openEdit(o);
    else openReservationsDialog(o);
  };
  const openCreateForDay = (iso: string) => {
    setEditingOrder(null);
    setForm({ orderNumber: '', productId: '', recipeId: '', quantity: 1, scheduledDate: iso, notes: '', lotCode: '', expirationDate: '' });
    setRecipes([]);
    setError(null);
    setSuccess(null);
    setCreateOpen(true);
  };

  const loadDayBlocks = async () => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const start = new Date(y, m, 1).toISOString();
    const end = new Date(y, m + 1, 0, 23, 59, 59).toISOString();
    try {
      const evs = await api.getCalendarEvents(start, end, ['custom']);
      setDayBlocks((evs || [])
        .filter((e: any) => e.category === 'block' || e.category === 'note')
        .map((e: any) => ({ id: e.id, title: e.title, iso: String(e.date).slice(0, 10), kind: e.category as 'block' | 'note' })));
    } catch {
      setDayBlocks([]);
    }
  };
  useEffect(() => {
    if (tab === 'calendar') loadDayBlocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, calendarMonth]);

  const blocksByDay = useMemo(() => {
    const map = new Map<string, { id: number; title: string; iso: string; kind: 'block' | 'note' }[]>();
    for (const b of dayBlocks) {
      if (!map.has(b.iso)) map.set(b.iso, []);
      map.get(b.iso)!.push(b);
    }
    return map;
  }, [dayBlocks]);

  const dayHasHardBlock = (iso: string) => (blocksByDay.get(iso) || []).some(b => b.kind === 'block');

  // Drag & drop de OPs entre días. Con distancia de activación para que el
  // clic simple siga abriendo el menú / la edición.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const handleOrderDragEnd = async (event: DragEndEvent) => {
    const order: ProductionOrder | undefined = event.active?.data?.current?.order;
    const targetIso: string | undefined = event.over?.data?.current?.iso;
    if (!order || !targetIso) return;
    const currentIso = (order.scheduledDate || '').slice(0, 10);
    if (targetIso === currentIso) return;
    if (dayHasHardBlock(targetIso)) {
      setError(`El día ${fmtIso(targetIso)} está bloqueado: no admite órdenes de producción.`);
      return;
    }
    const label = order.orderNumber || `#${order.id}`;
    if (!window.confirm(`¿Mover ${label} (${order.productName}) del ${fmtIso(currentIso)} al ${fmtIso(targetIso)}?`)) return;
    try {
      // Mediodía UTC para que el día no se corra por zona horaria.
      await api.updateProductionOrder(order.id, { scheduledDate: `${targetIso}T12:00:00.000Z` });
      setError(null);
      setSuccess(`${label} reprogramada al ${fmtIso(targetIso)}.`);
      await loadOrders();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo reprogramar la orden');
    }
  };

  const saveBlock = async () => {
    if (!blockDialog || !blockDialog.text.trim()) return;
    try {
      // Mediodía UTC para que el día no se corra por zona horaria en ningún calendario.
      await api.createCalendarEvent({
        title: blockDialog.text.trim(),
        date: `${blockDialog.iso}T12:00:00.000Z`,
        category: blockDialog.kind,
        color: blockDialog.kind === 'block' ? '#6b7280' : '#9ca3af',
        status: 'Pending',
      });
      setBlockDialog(null);
      await loadDayBlocks();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar el bloqueo');
    }
  };
  const deleteBlock = async (id: number) => {
    if (!window.confirm('¿Quitar esta nota/bloqueo del día?')) return;
    try { await api.deleteCalendarEvent(id); await loadDayBlocks(); } catch { /* noop */ }
  };
  const fmtIso = (iso: string) => iso.split('-').reverse().join('/');

  const renderStatusChip = (status: string, stockShortage?: string | null) => {
    const chip = <Chip label={STATUS_LABELS[status] || status} size="small" color={STATUS_COLORS[status] || 'default'} />;
    if (stockShortage) {
      return <Tooltip title={`Falta: ${stockShortage}`}><span>{chip}</span></Tooltip>;
    }
    return chip;
  };

  const renderOrderActions = (order: ProductionOrder) => (
    <Stack direction="row" spacing={1}>
      {!!order.stockShortage && order.status !== 'Completed' && order.status !== 'Cancelled' && (
        <Tooltip title="Reintentar reservar stock">
          <span>
            <IconButton size="small" color="error" onClick={() => retryStock(order)}>
              <Replay fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {(order.status === 'Planned' || order.status === 'PausedStock') && (
        <Tooltip title="Iniciar OP (aunque falte stock)">
          <span>
            <IconButton size="small" onClick={() => startOrder(order)}>
              <PlayArrow fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {order.status === 'InProgress' && (
        <Tooltip title="Pausar como semielaborado (producción avanzada, pendiente de calidad/etiquetado)">
          <span>
            <IconButton size="small" color="secondary" onClick={() => pauseSemielaborado(order)}>
              <PauseCircleOutline fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {(order.status === 'Planned' || order.status === 'InProgress' || order.status === 'Semielaborado') && (
        <Tooltip title="Completar OP">
          <span>
            <IconButton size="small" color="success" onClick={() => completeOrder(order)}>
              <DoneAll fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {(order.status === 'Planned' || order.status === 'InProgress' || order.status === 'Semielaborado') && (
        <Tooltip title="Editar OP">
          <span>
            <IconButton size="small" onClick={() => openEdit(order)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {(order.status === 'Planned' || order.status === 'InProgress' || order.status === 'Semielaborado' || order.status === 'PausedStock') && (
        <Tooltip title="Cancelar OP">
          <span>
            <IconButton size="small" color="error" onClick={() => cancelOrder(order)}>
              <Cancel fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {(order.status === 'Completed' || order.status === 'Cancelled') && !order.isHidden && (
        <Tooltip title="Ocultar del listado (no toca el stock)">
          <span>
            <IconButton size="small" onClick={() => hideOrder(order)}>
              <VisibilityOff fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {order.isHidden && (
        <Tooltip title="Volver a mostrar en el listado">
          <span>
            <IconButton size="small" color="primary" onClick={() => unhideOrder(order)}>
              <Visibility fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {order.status !== 'Completed' && (
        <Tooltip title="Borrar OP">
          <span>
            <IconButton size="small" color="error" onClick={() => deleteOrder(order)}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Typography variant="h4" fontWeight={700}>Órdenes de producción</Typography>
        {tab === 'developments' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Autocomplete
              size="small"
              options={devCustomers}
              getOptionLabel={(c) => c.legalName || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={devCustomerFilter}
              onChange={(_, val) => { setDevCustomerFilter(val); setTimeout(loadDevelopments, 0); }}
              renderInput={(params) => <TextField {...params} label="Filtrar por cliente" />}
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" startIcon={<Add />} onClick={openNewDevelopment}>Nuevo desarrollo</Button>
          </Stack>
        ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            select
            size="small"
            label="Estado"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" size="small" onClick={hideAllCompleted}>Ocultar completadas</Button>
          <Button variant={showHidden ? 'contained' : 'outlined'} size="small" color="inherit" onClick={() => setShowHidden(v => !v)}>
            {showHidden ? 'Ocultar las ocultas' : 'Mostrar ocultas'}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingOrder(null); setForm({ orderNumber: '', productId: '', recipeId: '', quantity: 1, scheduledDate: today(), notes: '', lotCode: '', expirationDate: '' }); setRecipes([]); setCreateOpen(true); setError(null); setSuccess(null); }}>Nueva OP</Button>
        </Stack>
        )}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab value="orders" label="Órdenes" />
        <Tab value="calendar" label="Calendario" />
        <Tab value="batches" label="Lotes" />
        <Tab value="developments" label="Desarrollos" />
      </Tabs>

      {tab === 'calendar' && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={() => shiftMonth(-1)}><ChevronLeft /></IconButton>
                <Typography variant="h6" sx={{ minWidth: 160, textAlign: 'center', textTransform: 'capitalize' }}>
                  {MONTHS_ES[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                </Typography>
                <IconButton size="small" onClick={() => shiftMonth(1)}><ChevronRight /></IconButton>
                <Button size="small" onClick={() => { const d = new Date(); setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Hoy</Button>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {(['Planned', 'InProgress', 'Semielaborado', 'Completed', 'Cancelled', 'PausedStock'] as const).map(s => (
                  <Stack key={s} direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" color={STATUS_COLORS[s]} label="" sx={{ width: 14, height: 14, '& .MuiChip-label': { p: 0 } }} />
                    <Typography variant="caption" color="text.secondary">{STATUS_LABELS[s]}</Typography>
                  </Stack>
                ))}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip size="small" label="" sx={{ width: 14, height: 14, bgcolor: '#6b7280', '& .MuiChip-label': { p: 0 } }} />
                  <Typography variant="caption" color="text.secondary">Día bloqueado</Typography>
                </Stack>
              </Stack>
            </Stack>

            <DndContext sensors={dndSensors} onDragEnd={handleOrderDragEnd}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {WEEKDAYS_ES.map(d => (
                <Typography key={d} variant="caption" fontWeight={700} color="text.secondary" sx={{ textAlign: 'center', py: 0.5 }}>{d}</Typography>
              ))}
              {calendarWeeks.flat().map(({ date, iso, inMonth }) => {
                const dayOrders = ordersByDay.get(iso) || [];
                const isToday = iso === todayIso;
                return (
                  <DroppableDayCell
                    key={iso}
                    iso={iso}
                    onClick={(e) => setDayMenu({ anchorEl: e.currentTarget, iso })}
                    title="Nueva orden o bloquear este día"
                    sx={{
                      minHeight: 92,
                      border: '1px solid',
                      borderColor: isToday ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      p: 0.5,
                      bgcolor: (dayHasHardBlock(iso) ? 'rgba(107,114,128,0.10)' : (inMonth ? 'background.paper' : 'action.hover')),
                      opacity: inMonth ? 1 : 0.6,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Typography variant="caption" fontWeight={isToday ? 700 : 400} color={isToday ? 'primary.main' : 'text.secondary'} sx={{ textAlign: 'right' }}>
                      {date.getDate()}
                    </Typography>
                    {(blocksByDay.get(iso) || []).map(b => (
                      <Tooltip key={`b${b.id}`} title={`${b.kind === 'block' ? 'Día bloqueado' : 'Nota'}: ${b.title} (clic para quitar)`}>
                        <Chip
                          size="small"
                          icon={b.kind === 'block' ? <BlockIcon sx={{ fontSize: 12 }} /> : <NotesIcon sx={{ fontSize: 12 }} />}
                          label={b.title}
                          onClick={(e) => { e.stopPropagation(); deleteBlock(b.id); }}
                          sx={{ maxWidth: '100%', height: 20, bgcolor: b.kind === 'block' ? '#6b7280' : '#9ca3af', color: 'white', cursor: 'pointer', '& .MuiChip-icon': { color: 'white' }, '& .MuiChip-label': { px: 0.5, fontSize: 11 } }}
                        />
                      </Tooltip>
                    ))}
                    {dayOrders.slice(0, 4).map(o => (
                      <DraggableOrderChip
                        key={o.id}
                        order={o}
                        tooltip={`${o.orderNumber ? `${o.orderNumber} · ` : ''}${o.productName} · ${o.quantity}u · ${STATUS_LABELS[o.status] || o.status}`}
                        onClick={() => onCalendarOrderClick(o)}
                      />
                    ))}
                    {dayOrders.length > 4 && (
                      <Typography variant="caption" color="text.secondary">+{dayOrders.length - 4} más</Typography>
                    )}
                  </DroppableDayCell>
                );
              })}
            </Box>
            </DndContext>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Clic en un día para crear una orden, dejar una nota o bloquearlo (ej. "No se trabaja"). Las notas son solo informativas; un día bloqueado no admite órdenes de producción. Arrastrá una orden planificada o en progreso a otro día para reprogramarla. Clic en una orden para editarla o ver sus reservas; clic en una nota o bloqueo para quitarlo.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Menu open={!!dayMenu} anchorEl={dayMenu?.anchorEl} onClose={() => setDayMenu(null)}>
        <MenuItem
          disabled={!!dayMenu && dayHasHardBlock(dayMenu.iso)}
          onClick={() => { const iso = dayMenu!.iso; setDayMenu(null); openCreateForDay(iso); }}
        >
          {dayMenu && dayHasHardBlock(dayMenu.iso) ? 'Día bloqueado — no admite órdenes' : 'Nueva orden de producción'}
        </MenuItem>
        <MenuItem onClick={() => { const iso = dayMenu!.iso; setDayMenu(null); setBlockDialog({ iso, text: '', kind: 'note' }); }}>Nota del día</MenuItem>
        <MenuItem onClick={() => { const iso = dayMenu!.iso; setDayMenu(null); setBlockDialog({ iso, text: '', kind: 'block' }); }}>Bloquear día (no admite órdenes)</MenuItem>
      </Menu>

      <Dialog open={!!batchEdit} onClose={() => setBatchEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar lote</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Código de lote"
              value={batchEdit?.lotCode || ''}
              inputProps={{ maxLength: 100 }}
              onChange={e => setBatchEdit(d => (d ? { ...d, lotCode: e.target.value } : d))}
              fullWidth
            />
            <TextField
              label="Vencimiento"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={batchEdit?.expirationDate || ''}
              onChange={e => setBatchEdit(d => (d ? { ...d, expirationDate: e.target.value } : d))}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              La corrección queda registrada en Movimientos con tu usuario y los valores anteriores.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveBatchEdit} disabled={batchEditSaving}>{batchEditSaving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!semielaboradoDialog} onClose={() => setSemielaboradoDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Pausar como semielaborado</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              La producción física avanzó o terminó, pero queda pendiente de control de
              calidad/etiquetado. No suma stock de producto terminado todavía.
            </Typography>
            <TextField
              label="¿En qué quedó el proceso? (opcional)"
              placeholder='Ej. "envasado, falta etiquetar"'
              multiline
              minRows={2}
              value={semielaboradoDialog?.note || ''}
              onChange={(e) => setSemielaboradoDialog(d => (d ? { ...d, note: e.target.value } : d))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSemielaboradoDialog(null)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={confirmPauseSemielaborado} disabled={semielaboradoSaving}>
            {semielaboradoSaving ? 'Guardando…' : 'Pausar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!blockDialog} onClose={() => setBlockDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{blockDialog?.kind === 'block' ? 'Bloquear día' : 'Nota del día'} {blockDialog ? fmtIso(blockDialog.iso) : ''}</DialogTitle>
        <DialogContent>
          {blockDialog?.kind === 'block' && (
            <Alert severity="warning" sx={{ mb: 1 }}>Un día bloqueado no admite órdenes de producción (ni nuevas ni reprogramadas).</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Texto"
            placeholder={blockDialog?.kind === 'block' ? 'Ej. No se trabaja, Limpieza, Mantenimiento' : 'Ej. Producción Creatina 350g: 1000 U'}
            value={blockDialog?.text || ''}
            inputProps={{ maxLength: 60 }}
            onChange={e => setBlockDialog(d => (d ? { ...d, text: e.target.value } : d))}
            onKeyDown={e => { if (e.key === 'Enter') saveBlock(); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveBlock} disabled={!blockDialog?.text.trim()}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!newDevDialog} onClose={() => setNewDevDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo desarrollo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre del desarrollo"
              placeholder='Ej. "Barrita proteica sabor choco"'
              value={newDevDialog?.name || ''}
              onChange={(e) => setNewDevDialog(d => (d ? { ...d, name: e.target.value } : d))}
              fullWidth
            />
            <Autocomplete
              options={devCustomers}
              getOptionLabel={(c) => c.legalName || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={newDevDialog?.customer || null}
              onChange={(_, val) => setNewDevDialog(d => (d ? { ...d, customer: val } : d))}
              renderInput={(params) => <TextField {...params} label="Cliente" />}
              fullWidth
            />
            <TextField
              label="Notas del desarrollo (opcional)"
              value={newDevDialog?.notes || ''}
              onChange={(e) => setNewDevDialog(d => (d ? { ...d, notes: e.target.value } : d))}
              fullWidth
            />
            <Typography variant="subtitle2">Primera muestra</Typography>
            <TextField
              label="Fecha de elaboración"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newDevDialog?.elaborationDate || ''}
              onChange={(e) => setNewDevDialog(d => (d ? { ...d, elaborationDate: e.target.value } : d))}
              fullWidth
            />
            <TextField
              label="Notas de la muestra (opcional)"
              value={newDevDialog?.sampleNotes || ''}
              onChange={(e) => setNewDevDialog(d => (d ? { ...d, sampleNotes: e.target.value } : d))}
              fullWidth
            />
            <Typography variant="subtitle2">Materia prima usada</Typography>
            {newDevDialog?.rows.map((row, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  options={devMaterials}
                  getOptionLabel={(m) => m.name || ''}
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
                  value={devMaterials.find(m => String(m.id) === String(row.materialId)) || null}
                  onChange={(_, val) => setNewDevDialog(d => (d ? { ...d, rows: d.rows.map((r, i) => i === idx ? { ...r, materialId: val ? val.id : '' } : r) } : d))}
                  renderInput={(params) => <TextField {...params} label="Materia prima" size="small" />}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Cantidad"
                  type="number"
                  size="small"
                  value={row.quantity}
                  onChange={(e) => setNewDevDialog(d => (d ? { ...d, rows: d.rows.map((r, i) => i === idx ? { ...r, quantity: e.target.value } : r) } : d))}
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => setNewDevDialog(d => (d ? { ...d, rows: d.rows.filter((_, i) => i !== idx) } : d))} disabled={(newDevDialog?.rows.length || 0) <= 1}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" onClick={() => setNewDevDialog(d => (d ? { ...d, rows: [...d.rows, emptyDevRow()] } : d))}>+ Agregar materia prima</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDevDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveNewDevelopment} disabled={newDevSaving}>{newDevSaving ? 'Guardando…' : 'Crear desarrollo'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!addSampleDialog} onClose={() => setAddSampleDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva muestra — {addSampleDialog?.developmentName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Fecha de elaboración"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={addSampleDialog?.elaborationDate || ''}
              onChange={(e) => setAddSampleDialog(d => (d ? { ...d, elaborationDate: e.target.value } : d))}
              fullWidth
            />
            <TextField
              label="Notas (opcional)"
              value={addSampleDialog?.notes || ''}
              onChange={(e) => setAddSampleDialog(d => (d ? { ...d, notes: e.target.value } : d))}
              fullWidth
            />
            <Typography variant="subtitle2">Materia prima usada</Typography>
            {addSampleDialog?.rows.map((row, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  options={devMaterials}
                  getOptionLabel={(m) => m.name || ''}
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
                  value={devMaterials.find(m => String(m.id) === String(row.materialId)) || null}
                  onChange={(_, val) => setAddSampleDialog(d => (d ? { ...d, rows: d.rows.map((r, i) => i === idx ? { ...r, materialId: val ? val.id : '' } : r) } : d))}
                  renderInput={(params) => <TextField {...params} label="Materia prima" size="small" />}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Cantidad"
                  type="number"
                  size="small"
                  value={row.quantity}
                  onChange={(e) => setAddSampleDialog(d => (d ? { ...d, rows: d.rows.map((r, i) => i === idx ? { ...r, quantity: e.target.value } : r) } : d))}
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => setAddSampleDialog(d => (d ? { ...d, rows: d.rows.filter((_, i) => i !== idx) } : d))} disabled={(addSampleDialog?.rows.length || 0) <= 1}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" onClick={() => setAddSampleDialog(d => (d ? { ...d, rows: [...d.rows, emptyDevRow()] } : d))}>+ Agregar materia prima</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSampleDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveAddSample} disabled={addSampleSaving}>{addSampleSaving ? 'Guardando…' : 'Agregar muestra'}</Button>
        </DialogActions>
      </Dialog>

      {tab === 'batches' && (
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Buscar lote/referencia" value={batchSearch} onChange={e => setBatchSearch(e.target.value)} sx={{ minWidth: 220 }} />
              <TextField size="small" type="date" label="Vence desde" InputLabelProps={{ shrink: true }} value={batchExpFrom} onChange={e => setBatchExpFrom(e.target.value)} sx={{ minWidth: 160 }} />
              <TextField size="small" type="date" label="Vence hasta" InputLabelProps={{ shrink: true }} value={batchExpTo} onChange={e => setBatchExpTo(e.target.value)} sx={{ minWidth: 160 }} />
              <TextField size="small" label="Orden de producción" value={batchProdOrderId} onChange={e => setBatchProdOrderId(e.target.value.replace(/\D/g, ''))} sx={{ width: 180 }} />
              <TextField size="small" select label="Disponibilidad" value={batchOnlyAvailable ? 'available' : 'all'} onChange={e => setBatchOnlyAvailable(e.target.value === 'available')} sx={{ minWidth: 180 }}>
                <MenuItem value="available">Con stock</MenuItem>
                <MenuItem value="all">Todos</MenuItem>
              </TextField>
              <Button variant="contained" onClick={loadBatches} disabled={batchesLoading}>Aplicar filtros</Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lote</TableCell>
                    <TableCell>Material</TableCell>
                    <TableCell align="right">Stock</TableCell>
                    <TableCell>Vencimiento</TableCell>
                    <TableCell>Recepción</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>Consumido por OP</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.map(b => (
                    <TableRow key={b.id} hover>
                      <TableCell>{b.lotCode || b.publicId || `#${b.id}`}</TableCell>
                      <TableCell>{b.materialName || `#${b.materialId}`}</TableCell>
                      <TableCell align="right">{b.quantity}</TableCell>
                      <TableCell>{b.expirationDate ? new Date(b.expirationDate).toLocaleDateString('es-AR') : '—'}</TableCell>
                      <TableCell>{b.purchaseReference || '—'}</TableCell>
                      <TableCell>{b.supplierName || '—'}</TableCell>
                      <TableCell>
                        {b.consumedBy.length === 0 ? '—' : (
                          <Stack spacing={0.25}>
                            {b.consumedBy.map((c, i) => (
                              <Typography key={i} variant="caption">OP #{c.productionOrderId}: {c.quantityUsed}</Typography>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {canEditBatches && (
                          <IconButton size="small" onClick={() => setBatchEdit({ id: b.id, lotCode: b.lotCode || '', expirationDate: b.expirationDate ? String(b.expirationDate).slice(0, 10) : '' })} title="Editar lote / vencimiento">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" color="error" onClick={() => deleteBatch(b)} title="Eliminar lote">
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {batches.length === 0 && !batchesLoading && (
                    <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">Sin lotes para los filtros aplicados.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab === 'developments' && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Materia prima gastada en pruebas/desarrollos para un cliente.
            </Typography>
            {devLoading && <Typography variant="body2" color="text.secondary">Cargando…</Typography>}
            {!devLoading && developments.length === 0 && (
              <Typography variant="body2" color="text.secondary">Sin desarrollos cargados todavía.</Typography>
            )}
            <Stack spacing={2}>
              {developments.map(dev => (
                <Card key={dev.id} variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                      <Box sx={{ cursor: 'pointer' }} onClick={() => setExpandedDev(expandedDev === dev.id ? null : dev.id)}>
                        <Typography variant="h6">{dev.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cliente: {dev.customerName} · {dev.samples.length} muestra{dev.samples.length === 1 ? '' : 's'} · Costo total: ${dev.totalCost.toLocaleString()}
                          {dev.lastSampleDate ? ` · Última: ${formatDate(dev.lastSampleDate)}` : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => openAddSample(dev)}>+ Muestra</Button>
                        <Button size="small" color="error" onClick={() => deleteDevelopment(dev)}>Borrar</Button>
                        <Button size="small" onClick={() => setExpandedDev(expandedDev === dev.id ? null : dev.id)}>
                          {expandedDev === dev.id ? 'Ocultar' : 'Ver muestras'}
                        </Button>
                      </Stack>
                    </Stack>
                    {expandedDev === dev.id && (
                      <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Fecha</TableCell>
                              <TableCell>Materia prima usada</TableCell>
                              <TableCell align="right">Costo</TableCell>
                              <TableCell>Notas</TableCell>
                              <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dev.samples.map(s => (
                              <TableRow key={s.id}>
                                <TableCell>{formatDate(s.elaborationDate)}</TableCell>
                                <TableCell>
                                  <Stack spacing={0.25}>
                                    {s.materialUsages.map((u, i) => (
                                      <Typography key={i} variant="caption">{u.materialName}: {u.quantity}</Typography>
                                    ))}
                                  </Stack>
                                </TableCell>
                                <TableCell align="right">${s.totalCost.toLocaleString()}</TableCell>
                                <TableCell>{s.notes || '—'}</TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" color="error" onClick={() => deleteDevSample(s.id)}>
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {tab === 'orders' && (
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Listado</Typography>
            <Button variant="text" onClick={loadOrders} disabled={loading}>Actualizar</Button>
          </Stack>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><PlaylistAddCheck color="primary" sx={{ fontSize: 40, opacity: 0.6 }} /></Stack>
          ) : isMobile ? (
            <Stack spacing={2}>
              {filteredOrders.map((order) => (
                <Card key={order.id} variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>{order.productName}</Typography>
                        <Typography variant="body2" color="text.secondary">{order.orderNumber || `#${order.id}`} · Cantidad: {order.quantity}</Typography>
                      </Box>
                      {renderStatusChip(order.status, order.stockShortage)}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">Planificada: {formatDate(order.scheduledDate)}</Typography>
                    <Typography variant="body2" color="text.secondary">Formulación: {order.recipeName || '—'}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button size="small" onClick={() => openReservationsDialog(order)}>Reservas</Button>
                      {renderOrderActions(order)}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {filteredOrders.length === 0 && (
                <Typography variant="body2" color="text.secondary">Sin órdenes registradas.</Typography>
              )}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Número</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Formulación</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Programada</TableCell>
                    <TableCell>Lote</TableCell>
                    <TableCell>Vencimiento</TableCell>
                    <TableCell>Reservas</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{order.orderNumber || `#${order.id}`}</TableCell>
                      <TableCell>{order.productName}</TableCell>
                      <TableCell>{order.recipeName || '—'}</TableCell>
                      <TableCell align="right">{order.quantity}</TableCell>
                      <TableCell>{renderStatusChip(order.status, order.stockShortage)}</TableCell>
                      <TableCell>{formatDate(order.scheduledDate)}</TableCell>
                      <TableCell>{order.lotCode || '—'}</TableCell>
                      <TableCell>{formatMonthYear(order.expirationDate)}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => openReservationsDialog(order)}>Ver</Button>
                      </TableCell>
                      <TableCell align="right">{renderOrderActions(order)}</TableCell>
                    </TableRow>
                  ))}
                  {filteredOrders.length === 0 && (
                    <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary">Sin órdenes registradas.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      )}

      <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOrder ? `Editar orden ${editingOrder.orderNumber || `#${editingOrder.id}`}` : 'Nueva orden de producción'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingOrder && editingOrder.status !== 'Planned' && (
              <Alert severity="info">
                {editingOrder.status === 'InProgress' || editingOrder.status === 'Semielaborado'
                  ? (canEditInProgressQuantity
                    ? 'Esta orden ya está en curso: la formulación no se puede cambiar (cancelala y creá una nueva), pero sí podés ajustar la cantidad planificada si el rendimiento real difiere.'
                    : 'Esta orden ya está en curso: solo podés cambiar la fecha y las notas. Tu rol no puede ajustar la cantidad de una orden en curso.')
                  : 'Esta orden ya fue finalizada: solo podés cambiar la fecha y las notas.'}
              </Alert>
            )}
            {editingOrder && (
              <TextField
                label="Número"
                value={form.orderNumber}
                onChange={(e) => setForm((prev) => ({ ...prev, orderNumber: e.target.value }))}
                helperText="Editable por si necesitás alinearlo con un correlativo propio (ej. de otro sistema)"
                fullWidth
              />
            )}
            <Autocomplete
              options={products}
              getOptionLabel={(p) => p.name || ''}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
              value={products.find((p) => String(p.id) === String(form.productId)) || null}
              onChange={(_, val) => handleProductChange(val ? String(val.id) : '')}
              renderInput={(params) => <TextField {...params} label="Producto" />}
              disabled={!!editingOrder}
              fullWidth
            />
            <Autocomplete
              options={recipes}
              getOptionLabel={(r) => r.name || ''}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
              value={recipes.find((r) => String(r.id) === String(form.recipeId)) || null}
              onChange={(_, val) => setForm((prev) => ({ ...prev, recipeId: val ? String(val.id) : '' }))}
              renderInput={(params) => <TextField {...params} label="Formulación" />}
              disabled={!form.productId || (!!editingOrder && editingOrder.status !== 'Planned')}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Cantidad"
                type="number"
                value={form.quantity}
                onChange={(e) => setForm((prev) => ({ ...prev, quantity: Number(e.target.value) }))}
                disabled={!!editingOrder && editingOrder.status !== 'Planned'
                  && !((editingOrder.status === 'InProgress' || editingOrder.status === 'Semielaborado') && canEditInProgressQuantity)}
                fullWidth
              />
              <TextField
                label="Fecha programada"
                type="date"
                value={form.scheduledDate}
                onChange={(e) => setForm((prev) => ({ ...prev, scheduledDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Lote planificado"
                value={form.lotCode}
                onChange={(e) => setForm((prev) => ({ ...prev, lotCode: e.target.value }))}
                helperText="Se usa como lote del producto terminado al completar la orden"
                fullWidth
              />
              <TextField
                label="Vencimiento planificado"
                type="month"
                value={form.expirationDate}
                onChange={(e) => setForm((prev) => ({ ...prev, expirationDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Notas"
              multiline
              minRows={2}
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!canCreate || saving}>
            {saving ? 'Guardando...' : editingOrder ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reservationsOpen} onClose={() => setReservationsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reservas — {selectedOrder?.orderNumber ? `${selectedOrder.orderNumber} · ` : ''}{selectedOrder?.productName}</DialogTitle>
        <DialogContent>
          {selectedOrder?.actualQuantity != null && (
            <Alert severity={selectedOrder.yieldVariance ? (selectedOrder.yieldVariance < 0 ? 'warning' : 'info') : 'success'} sx={{ mb: 2 }}>
              Planificado: {selectedOrder.quantity} · Real: {selectedOrder.actualQuantity}
              {!!selectedOrder.yieldVariance && (
                <> · {selectedOrder.yieldVariance < 0 ? 'Merma' : 'Excedente'}: {Math.abs(selectedOrder.yieldVariance)}</>
              )}
              {selectedOrder.yieldVarianceReason && <> · Motivo: {selectedOrder.yieldVarianceReason}</>}
            </Alert>
          )}
          {selectedOrder?.reservations.length ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Componente</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOrder.reservations.map((res) => (
                    <TableRow key={res.id}>
                      <TableCell>{res.materialName || res.productName}</TableCell>
                      <TableCell align="right">{res.quantityRequired}</TableCell>
                      <TableCell>{res.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">Sin componentes asociados.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReservationsOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Completar producción — {completeOrderState?.productName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Propuesta de consumo FEFO (vencimiento más cercano primero). Podés ajustar
            las cantidades por lote. La suma por material debe igualar la cantidad requerida.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Lote del producto terminado"
              value={completeLotCode}
              onChange={(e) => setCompleteLotCode(e.target.value)}
              fullWidth
            />
            <TextField
              label="Vencimiento del producto terminado"
              type="month"
              value={completeExpirationDate}
              onChange={(e) => setCompleteExpirationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
            <TextField
              label="Cantidad real producida"
              type="number"
              value={completeActualQuantity}
              onChange={(e) => setCompleteActualQuantity(e.target.value)}
              helperText={`Planificado: ${completeOrderState?.quantity ?? '—'}`}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            {completeActualQuantity !== '' && completeOrderState && Number(completeActualQuantity) !== completeOrderState.quantity && (
              <TextField
                label="Motivo de la merma/excedente"
                value={completeYieldReason}
                onChange={(e) => setCompleteYieldReason(e.target.value)}
                required={Number(completeActualQuantity) < completeOrderState.quantity}
                error={Number(completeActualQuantity) < completeOrderState.quantity && !completeYieldReason.trim()}
                helperText={Number(completeActualQuantity) < completeOrderState.quantity ? 'Obligatorio: hubo merma respecto de lo planificado' : 'Opcional (hubo excedente)'}
                fullWidth
              />
            )}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Muestra de retención"
              type="number"
              value={completeRetentionQty}
              onChange={(e) => setCompleteRetentionQty(e.target.value)}
              helperText="Se descuenta del lote, no queda vendible"
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Muestra para análisis"
              type="number"
              value={completeAnalysisQty}
              onChange={(e) => setCompleteAnalysisQty(e.target.value)}
              helperText="Concepto separado de la retención"
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Materias primas — propuesta FEFO (vencimiento más cercano primero). Podés ajustar
            las cantidades por lote; la suma por material debe igualar la cantidad requerida.
          </Typography>
          {completeRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">Sin materiales con reservas activas.</Typography>
          )}
          {completeRows.map((row, ri) => {
            const sum = row.batches.reduce((s, b) => s + (Number(b.assigned) || 0), 0) + (Number(row.untrackedAssigned) || 0);
            const remaining = Math.round((row.quantityRequired - sum) * 1000) / 1000;
            return (
              <Box key={row.materialId} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{row.materialName}</Typography>
                  <Typography variant="caption" color={Math.abs(remaining) > 0.001 ? 'error.main' : 'success.main'}>
                    Asignado {sum} / Requerido {row.quantityRequired} ({remaining > 0 ? `faltan ${remaining}` : remaining < 0 ? `sobran ${Math.abs(remaining)}` : 'OK'})
                  </Typography>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lote</TableCell>
                      <TableCell>Vencimiento</TableCell>
                      <TableCell align="right">Disponible</TableCell>
                      <TableCell align="right">Asignar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.batches.map((b, bi) => (
                      <TableRow key={b.batchId}>
                        <TableCell>{b.publicId || `#${b.batchId}`}</TableCell>
                        <TableCell>{b.expirationDate ? new Date(b.expirationDate).toLocaleDateString('es-AR') : '—'}</TableCell>
                        <TableCell align="right">{b.availableQuantity}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={b.assigned}
                            onChange={(e) => {
                              const v = Math.max(0, Number(e.target.value || 0));
                              setCompleteRows(prev => prev.map((r, i) => i === ri ? {
                                ...r,
                                batches: r.batches.map((bb, j) => j === bi ? { ...bb, assigned: v } : bb),
                              } : r));
                            }}
                            inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' } }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {row.untrackedAvailable > 0 && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2">Sin lote</Typography>
                          <Typography variant="caption" color="text.secondary">Stock suelto, sin lote cargado</Typography>
                        </TableCell>
                        <TableCell align="right">{row.untrackedAvailable}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={row.untrackedAssigned}
                            onChange={(e) => {
                              const v = Math.max(0, Number(e.target.value || 0));
                              setCompleteRows(prev => prev.map((r, i) => i === ri ? { ...r, untrackedAssigned: v } : r));
                            }}
                            inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' } }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    {row.batches.length === 0 && row.untrackedAvailable <= 0 && (
                      <TableRow><TableCell colSpan={4}><Typography variant="caption" color="error.main">Sin lotes ni stock suelto disponible para este material</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            );
          })}

          {completeProductRows.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                Componentes tipo producto (granel/semielaborado) — propuesta FIFO por fecha de
                ingreso. Podés elegir manualmente otro lote (ej. liquidar uno parcial) en vez de
                la propuesta automática.
              </Typography>
              {completeProductRows.map((row, ri) => {
                const sum = row.batches.reduce((s, b) => s + (Number(b.assigned) || 0), 0);
                const remaining = Math.round((row.quantityRequired - sum) * 1000) / 1000;
                return (
                  <Box key={row.productId} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">{row.productName}</Typography>
                      <Typography variant="caption" color={Math.abs(remaining) > 0.001 ? 'error.main' : 'success.main'}>
                        Asignado {sum} / Requerido {row.quantityRequired} ({remaining > 0 ? `faltan ${remaining}` : remaining < 0 ? `sobran ${Math.abs(remaining)}` : 'OK'})
                      </Typography>
                    </Stack>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Lote</TableCell>
                          <TableCell>Vencimiento</TableCell>
                          <TableCell align="right">Disponible</TableCell>
                          <TableCell align="right">Asignar</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row.batches.map((b, bi) => (
                          <TableRow key={b.batchId}>
                            <TableCell>{b.publicId || `#${b.batchId}`}</TableCell>
                            <TableCell>{b.expirationDate ? new Date(b.expirationDate).toLocaleDateString('es-AR') : '—'}</TableCell>
                            <TableCell align="right">{b.availableQuantity}</TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={b.assigned}
                                onChange={(e) => {
                                  const v = Math.max(0, Number(e.target.value || 0));
                                  setCompleteProductRows(prev => prev.map((r, i) => i === ri ? {
                                    ...r,
                                    batches: r.batches.map((bb, j) => j === bi ? { ...bb, assigned: v } : bb),
                                  } : r));
                                }}
                                inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' } }}
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        {row.batches.length === 0 && (
                          <TableRow><TableCell colSpan={4}><Typography variant="caption" color="error.main">Sin lotes disponibles para este producto</Typography></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={completeSaving} onClick={confirmComplete}>
            {completeSaving ? 'Procesando…' : 'Confirmar y completar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
